import { useState } from "react";
import { useTranslation } from "react-i18next";
import { HEX_PATTERN, KIT_PRESETS } from "./kitPresets";

interface Props {
  /** `#RRGGBB`, or null/undefined when no colour is set. */
  value: string | null | undefined;
  onChange: (hex: string | null) => void;
  testId: string;
  /** Show a "no colour" action (an explicit, resolved empty). */
  allowClear?: boolean;
  invalid?: boolean;
}

/** Preset swatches plus a hex field for one kit part (EPIC-112 D5/D7). */
export default function KitColorPicker({
  value,
  onChange,
  testId,
  allowClear = true,
  invalid,
}: Props) {
  const { t } = useTranslation("admin");
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value ?? "";
  const hexInvalid = draft !== null && draft !== "" && !HEX_PATTERN.test(draft);

  return (
    <div
      className={`flex flex-wrap items-center gap-1 ${
        invalid ? "rounded ring-2 ring-red-400 p-1" : ""
      }`}
      data-testid={testId}
    >
      {KIT_PRESETS.map((p) => (
        <button
          key={p.hex}
          type="button"
          onClick={() => {
            setDraft(null);
            onChange(p.hex);
          }}
          title={t(`matchSheetImport.color.${p.key}`)}
          aria-label={t(`matchSheetImport.color.${p.key}`)}
          aria-pressed={value?.toLowerCase() === p.hex}
          data-testid={`${testId}-swatch-${p.hex.slice(1)}`}
          className={`h-6 w-6 rounded-full border ${
            value?.toLowerCase() === p.hex
              ? "ring-2 ring-primary-600 ring-offset-1"
              : "border-gray-300"
          }`}
          style={{ backgroundColor: p.hex }}
        />
      ))}
      <input
        type="text"
        inputMode="text"
        maxLength={7}
        value={shown}
        placeholder="#RRGGBB"
        aria-label={t("matchSheetImport.hexInput")}
        data-testid={`${testId}-hex`}
        onChange={(e) => {
          const next = e.target.value.trim();
          setDraft(next);
          if (HEX_PATTERN.test(next)) {
            onChange(next.toLowerCase());
            setDraft(null);
          }
        }}
        onBlur={() => setDraft(null)}
        className={`input w-24 text-xs ${hexInvalid ? "border-red-500" : ""}`}
      />
      {allowClear && (
        <button
          type="button"
          onClick={() => {
            setDraft(null);
            onChange(null);
          }}
          className="text-xs text-gray-500 underline"
          data-testid={`${testId}-clear`}
        >
          {t("matchSheetImport.noColor")}
        </button>
      )}
    </div>
  );
}
